import React from "react";
import {
    Snowflake, Scale, Recycle, Rocket, ShieldCheck, Leaf, Zap, Heart,
    Award, Clock, TrendingUp, Package, Star, Truck, Sparkles, Target,
    CheckCircle, Droplets,
} from "lucide-react";
import { useSiteContent, getSection } from "../../context/SiteContentContext";

// Whitelist of Lucide icons exposed to the CMS. Must match the backend
// DIFF_ICON_KEYS list in core/cms.py.
const ICON_MAP = {
    Scale, Snowflake, Recycle, Rocket, ShieldCheck, Leaf, Zap, Heart,
    Award, Clock, TrendingUp, Package, Star, Truck, Sparkles, Target,
    CheckCircle, Droplets,
};

// Styling per variant. First item in the list is rendered in a large slot,
// items 2 & 3 small, item 4 medium. Variants control colours only.
const VARIANT_STYLES = {
    light: {
        bg: "bg-white",
        ring: "ring-brand-green/20",
        accent: "text-brand-green",
        title: "text-brand-ink",
        text: "text-brand-ink-soft",
    },
    orange: {
        bg: "bg-brand-orange",
        ring: "ring-white/20",
        accent: "text-white",
        title: "text-white",
        text: "text-white/85",
    },
    "green-soft": {
        bg: "bg-brand-green-soft",
        ring: "ring-brand-green/10",
        accent: "text-brand-green",
        title: "text-brand-ink",
        text: "text-brand-ink-soft",
    },
    dark: {
        bg: "bg-brand-ink",
        ring: "ring-white/10",
        accent: "text-brand-orange",
        title: "text-white",
        text: "text-white/80",
    },
};

// Layout span per position (exactly 4 items expected).
const LAYOUT_SPANS = [
    "lg:col-span-2 lg:row-span-2",
    "lg:col-span-1",
    "lg:col-span-1",
    "lg:col-span-2",
];

const FALLBACK = {
    eyebrow: "O nosso diferencial",
    title_pre: "Não vendemos só sumos.",
    title_highlight: "Vendemos operação simples.",
    items: [
        { icon_key: "Scale", title: "Porções exactas", text: "Dose por dose, mesmo sabor, mesmo preço. Sem adivinhar, sem desperdício. O barista não precisa de pesar, descascar nem cortar.", variant: "light" },
        { icon_key: "Snowflake", title: "Conservação a -18 °C", text: "Validade longa. Stock seguro.", variant: "orange" },
        { icon_key: "Recycle", title: "Zero desperdício", text: "Sem fruta que estraga. Sem cascas.", variant: "green-soft" },
        { icon_key: "Rocket", title: "Pronto para escalar", text: "Operação standardizada. Mesmo resultado em 1 ou 100 pontos. Ideal para cadeias e redes de franchising.", variant: "dark" },
    ],
};

export const Differentiators = () => {
    const { content } = useSiteContent();
    const d = { ...FALLBACK, ...getSection(content, "differentiators", {}) };
    const items = Array.isArray(d.items) && d.items.length >= 1 ? d.items : FALLBACK.items;

    return (
        <section className="py-20 md:py-28 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl">
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                        {d.eyebrow}
                    </span>
                    <h2 className="mt-3 font-display font-medium text-4xl md:text-5xl text-brand-ink leading-tight">
                        {d.title_pre}{" "}
                        <span className="text-brand-green">{d.title_highlight}</span>
                    </h2>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2 gap-5 md:gap-6">
                    {items.map((it, i) => {
                        const Icon = ICON_MAP[it.icon_key] || Sparkles;
                        const styles = VARIANT_STYLES[it.variant] || VARIANT_STYLES.light;
                        const span = LAYOUT_SPANS[i] || "";
                        const slug = (it.title || `diff-${i}`)
                            .toLowerCase()
                            .replace(/\s/g, "-");
                        return (
                            <article
                                key={`${slug}-${i}`}
                                data-testid={`diff-${slug}`}
                                className={`relative rounded-2xl p-8 md:p-10 ring-1 ${styles.ring} ${span} ${styles.bg} overflow-hidden transition-all hover:-translate-y-1 duration-300`}
                            >
                                <div className="h-14 w-14 rounded-2xl bg-white/95 flex items-center justify-center shadow-soft">
                                    <Icon className={`h-6 w-6 ${styles.accent}`} />
                                </div>
                                <h3 className={`mt-6 font-display font-medium text-2xl md:text-3xl ${styles.title}`}>
                                    {it.title}
                                </h3>
                                <p className={`mt-3 text-base md:text-lg leading-relaxed ${styles.text}`}>
                                    {it.text}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
